/**
 * ActivityFeed — componentă WhatsApp-like reutilizabilă pentru cronologie.
 * Props:
 *   - category: 'all' | 'inventory' | 'psa' | 'hours' | 'fuel' (default 'all')
 *   - pageSize: numărul de evenimente per pagină (default 30)
 *   - compact: true → afișare compactă pentru embed în module (fără paginare vizibilă, buton „Vezi tot")
 *   - onViewAll: callback pentru butonul „Vezi tot" (doar când compact=true)
 *   - title: titlu opțional deasupra listei
 */
import React, { useEffect, useState, useRef, useCallback } from "react";
import { api } from "../lib/api";
import { fmtRoDate } from "../lib/dateFmt";
import { Loader2, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const COLOR_CLASSES = {
  blue: "bg-blue-50 border-blue-200 text-blue-900",
  green: "bg-green-50 border-green-200 text-green-900",
  amber: "bg-amber-50 border-amber-200 text-amber-900",
  red: "bg-red-50 border-red-200 text-red-900",
  purple: "bg-purple-50 border-purple-200 text-purple-900",
  sky: "bg-sky-50 border-sky-200 text-sky-900",
  indigo: "bg-indigo-50 border-indigo-200 text-indigo-900",
  orange: "bg-orange-50 border-orange-200 text-orange-900",
  emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
  zinc: "bg-zinc-50 border-zinc-200 text-zinc-800",
};

function fmtWhen(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
    const sameYesterday = d.toDateString() === yesterday.toDateString();
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    if (sameDay) return `Astăzi ${hh}:${mm}`;
    if (sameYesterday) return `Ieri ${hh}:${mm}`;
    return `${fmtRoDate(d.toISOString().slice(0, 10))} ${hh}:${mm}`;
  } catch { return iso; }
}

/**
 * Bulă de chat pentru un eveniment.
 * Layout: avatar-icon | [actor · timestamp] mesaj [pentru target]
 */
function ActivityBubble({ ev }) {
  const cc = COLOR_CLASSES[ev.color] || COLOR_CLASSES.zinc;
  return (
    <div
      className={`flex gap-3 border-l-4 ${cc} rounded-r-lg px-3 py-2.5 shadow-sm hover:shadow transition-shadow`}
      data-testid={`activity-bubble-${ev.id}`}
    >
      <div className="text-2xl leading-none flex-shrink-0 mt-0.5" aria-hidden>
        {ev.icon || "•"}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline flex-wrap gap-x-2 gap-y-0.5">
          <span className="font-bold text-sm">{ev.actor_name || "—"}</span>
          <span className="text-[11px] text-zinc-500 font-mono">{fmtWhen(ev.created_at)}</span>
        </div>
        <div className="text-sm mt-0.5">
          <span className="italic opacity-75">{ev.verb}</span>
          {ev.target_name && (
            <>
              {" "}
              <span className="opacity-60">pentru</span>{" "}
              <span className="font-semibold">{ev.target_name}</span>
            </>
          )}
        </div>
        <div className="text-[13px] text-zinc-700 mt-0.5 break-words">
          {ev.message}
        </div>
      </div>
    </div>
  );
}

export default function ActivityFeed({
  category = "all",
  endpoint = "/activity",
  pageSize = 30,
  compact = false,
  onViewAll = null,
  title = null,
}) {
  const [items, setItems] = useState([]);
  const [nextBefore, setNextBefore] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState("");
  const observerRef = useRef(null);
  const loadMoreRef = useRef(null);

  const load = useCallback(async (before) => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams();
      params.set("category", category);
      params.set("limit", String(compact ? Math.min(pageSize, 8) : pageSize));
      if (before) params.set("before", before);
      const { data } = await api.get(`${endpoint}?${params.toString()}`);
      const rows = data?.items || [];
      setItems((prev) => (before ? [...prev, ...rows] : rows));
      setNextBefore(data?.next_before || null);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Eroare la încărcare");
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  }, [category, pageSize, compact, endpoint]);

  useEffect(() => {
    setItems([]);
    setNextBefore(null);
    setInitialLoading(true);
    load(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  // Infinite scroll observer — doar când nu suntem în modul compact
  useEffect(() => {
    if (compact) return;
    if (!nextBefore) return;
    const el = loadMoreRef.current;
    if (!el) return;
    if (observerRef.current) observerRef.current.disconnect();
    const obs = new IntersectionObserver((entries) => {
      const first = entries[0];
      if (first?.isIntersecting && !loading) {
        load(nextBefore);
      }
    }, { rootMargin: "200px" });
    obs.observe(el);
    observerRef.current = obs;
    return () => obs.disconnect();
  }, [nextBefore, loading, compact, load]);

  if (initialLoading) {
    return (
      <div className="flex items-center justify-center py-8 text-zinc-500" data-testid="activity-loading">
        <Loader2 className="animate-spin" size={20}/>
        <span className="ml-2 text-sm">Încărcare cronologie…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-md text-sm" data-testid="activity-error">
        {error}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="space-y-2" data-testid={`activity-feed-${category}`}>
        {title && <h3 className="text-sm font-bold text-zinc-700 uppercase tracking-wider mb-2">{title}</h3>}
        <div className="text-center py-6 text-zinc-500 text-sm italic" data-testid="activity-empty">
          Nicio activitate înregistrată încă.
        </div>
        {compact && (onViewAll || category !== "all") && (
          <div className="pt-2">
            {onViewAll ? (
              <button
                type="button"
                onClick={onViewAll}
                className="w-full text-center text-sm text-blue-600 hover:text-blue-800 font-semibold py-2 hover:bg-blue-50 rounded transition-colors flex items-center justify-center gap-1"
                data-testid="activity-view-all"
              >
                Vezi toată cronologia <ArrowRight size={14}/>
              </button>
            ) : (
              <Link
                to={`/cronologie?category=${category}`}
                className="w-full text-center text-sm text-blue-600 hover:text-blue-800 font-semibold py-2 hover:bg-blue-50 rounded transition-colors flex items-center justify-center gap-1"
                data-testid="activity-view-all-link"
              >
                Vezi toată cronologia <ArrowRight size={14}/>
              </Link>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid={`activity-feed-${category}`}>
      {title && <h3 className="text-sm font-bold text-zinc-700 uppercase tracking-wider mb-2">{title}</h3>}
      {items.map((ev) => (
        <ActivityBubble key={ev.id} ev={ev}/>
      ))}
      {!compact && nextBefore && (
        <div ref={loadMoreRef} className="flex items-center justify-center py-4" data-testid="activity-load-more">
          {loading ? (
            <div className="flex items-center gap-2 text-zinc-500 text-sm">
              <Loader2 className="animate-spin" size={16}/> Se încarcă…
            </div>
          ) : (
            <button
              type="button"
              onClick={() => load(nextBefore)}
              className="text-sm text-blue-600 hover:text-blue-800 hover:underline font-semibold"
              data-testid="activity-load-more-btn"
            >
              Încarcă mai multe
            </button>
          )}
        </div>
      )}
      {compact && (onViewAll || category !== "all") && (
        <div className="pt-2">
          {onViewAll ? (
            <button
              type="button"
              onClick={onViewAll}
              className="w-full text-center text-sm text-blue-600 hover:text-blue-800 font-semibold py-2 hover:bg-blue-50 rounded transition-colors flex items-center justify-center gap-1"
              data-testid="activity-view-all"
            >
              Vezi toată cronologia <ArrowRight size={14}/>
            </button>
          ) : (
            <Link
              to={`/cronologie?category=${category}`}
              className="w-full text-center text-sm text-blue-600 hover:text-blue-800 font-semibold py-2 hover:bg-blue-50 rounded transition-colors flex items-center justify-center gap-1"
              data-testid="activity-view-all-link"
            >
              Vezi toată cronologia <ArrowRight size={14}/>
            </Link>
          )}
        </div>
      )}
    </div>
  );
}
